import { t } from '!/i18n'
import { config } from '@/config'
import type { BannerContentItemWithPrice } from '@/interfaces'
import {
  MediaTypes,
  type Banner,
  type BannerContentItem,
  type TvShow,
  type Vod,
} from '@setplex/tria-api'
import {
  type AdjustPurchaseableFn,
  type AdjustPurchaseableFnFactory,
} from '~/features/payment/sse-purchases'

function isBannerContentItemWithPrice(
  content: BannerContentItem
): content is BannerContentItemWithPrice {
  const bannerContent = content as BannerContentItemWithPrice

  return (
    bannerContent.minPrice !== undefined &&
    bannerContent.minCurrency !== undefined
  )
}

const extendMediaItemByUiPriceInfo = (
  item: BannerContentItem,
  adjust: AdjustPurchaseableFn
): BannerContentItemWithPrice | BannerContentItem => {
  if (isBannerContentItemWithPrice(item)) {
    const { minCurrency, minPrice } = item

    if (minPrice && minCurrency) {
      return {
        ...adjust(item),
        labelPrice: `${minCurrency} ${minPrice}+`,
        titleButton: t('watch_for', {
          price: minPrice,
          currency: minCurrency,
        }),
      }
    }
  }

  return adjust(item)
}

const formatBannerPrice = (
  banner: Banner,
  adjustFactory: AdjustPurchaseableFnFactory
): Banner => {
  if (banner.content) {
    const adjust: AdjustPurchaseableFn = (() => {
      switch (banner.contentType) {
        case MediaTypes.TV_CHANNEL:
          return adjustFactory('channel', 'channelSet')
        case MediaTypes.TV_SHOW:
          return adjustFactory('tvShow', 'tvShowSet')
        case MediaTypes.VOD:
          return adjustFactory('vod', 'vodSet')
        case MediaTypes.EPISODE:
          return adjustFactory('episode', 'tvShowSet')
        case MediaTypes.RADIO_CHANNEL:
          return adjustFactory('channel', 'channelSet')
        default:
          return (_) => _
      }
    })()

    return {
      ...banner,
      // issue due to ContentItem '@/interfaces' != ContentItem tria-api/interfaces/banners
      content: extendMediaItemByUiPriceInfo(banner.content, adjust),
    }
  }
  return banner
}

export const clearFieldsBanner =
  (adjustFactory: AdjustPurchaseableFnFactory = () => (_) => _) =>
  (banner: Banner): Banner => {
    const bannerWithPriceInfo = formatBannerPrice(banner, adjustFactory)

    // TODO: add check for content (no banner if no banner content)
    if (!config.tria_isBannersTitleAndDescriptionEnabled) {
      return {
        ...banner,
        content: removeBannerTextAndDescription(bannerWithPriceInfo.content),
      }
    }

    return bannerWithPriceInfo
  }

export const removeBannerTextAndDescription = (
  banner?: Banner['content']
): Banner['content'] | undefined => {
  if (banner) {
    return {
      ...banner,
      name: '',
      description: (banner as TvShow | Vod).description
        ? ''
        : (banner as TvShow | Vod).description,
    }
  }
}
